import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Typography,
  Button,
  TextField,
  Skeleton,
  Paper,
  Chip,
  InputAdornment,
  Stack,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Tooltip from '@mui/material/Tooltip';
import MainLayout from '@/components/MainLayout';
import MemberDialog from '@/components/MemberDialog';
import TagPill, { TAGS_EXPLAINER } from '@/components/TagPill';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { downloadCsv } from '@/lib/download';
import { toast } from '@/lib/toast';
import useNewParam from '@/lib/useNewParam';

const initials = (name) =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'lead', label: 'Leads' },
  { value: 'paused', label: 'Paused' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_META = {
  active: { label: 'Active', color: 'success.main' },
  lead: { label: 'Lead', color: 'info.main' },
  paused: { label: 'Paused', color: 'action.disabled' },
  cancelled: { label: 'Cancelled', color: 'error.main' },
};

const StatusCell = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, color: 'text.secondary' };
  const dim = status === 'paused' || status === 'cancelled';
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: meta.color, flexShrink: 0 }} />
      <Typography variant="body2" fontWeight={500} color={dim ? 'text.secondary' : 'text.primary'}>
        {meta.label}
      </Typography>
    </Stack>
  );
};

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

// Shared row template so the header and body columns always line up.
const ROW_COLUMNS = {
  display: 'grid',
  gridTemplateColumns: 'minmax(180px, 1.2fr) 1.4fr 1fr 110px 110px 110px 48px',
  gap: 1.5,
  alignItems: 'center',
  px: 2.5,
};

const MembersPage = () => {
  const { isOwner } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [activeMember, setActiveMember] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      await downloadCsv('/members/export', filterStatus === 'lead' ? 'leads' : 'members', params);
    } catch {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const fetchMembers = useCallback(async () => {
    try {
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const response = await api.get('/members', { params });
      setMembers(response.data);
    } catch {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const openCreate = () => {
    setDialogMode('create');
    setActiveMember(null);
    setDialogOpen(true);
  };

  useNewParam(openCreate);

  const openEdit = (member) => {
    setDialogMode('edit');
    setActiveMember(member);
    setDialogOpen(true);
  };

  const filteredMembers = members.filter(
    (m) =>
      m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (m.phone || '').includes(searchTerm)
  );

  return (
    <MainLayout title="Members" badge={loading ? undefined : members.length}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <MemberDialog
          open={dialogOpen}
          mode={dialogMode}
          variant="member"
          member={activeMember}
          onClose={() => setDialogOpen(false)}
          onSaved={fetchMembers}
        />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ md: 'center' }}
          sx={{ mb: 2.5 }}
        >
          <TextField
            placeholder="Search name, email or phone…"
            fullWidth
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="member-search-input"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 999 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0, overflowX: 'auto' }}>
            {FILTERS.map((f) => {
              const selected = filterStatus === f.value;
              return (
                <Chip
                  key={f.value}
                  label={f.label}
                  clickable
                  onClick={() => setFilterStatus(f.value)}
                  data-testid={`member-filter-${f.value}`}
                  sx={{
                    borderRadius: 999,
                    fontWeight: selected ? 600 : 500,
                    ...(selected
                      ? { bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.main' } }
                      : { bgcolor: 'transparent', border: 1, borderColor: 'divider', color: 'text.secondary' }),
                  }}
                />
              );
            })}
          </Stack>
          {isOwner && (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleExport}
              disabled={exporting}
              data-testid="export-members-button"
              sx={{ flexShrink: 0, borderColor: 'divider', color: 'text.secondary' }}
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openCreate}
            data-testid="add-member-button"
            sx={{ flexShrink: 0 }}
          >
            Add member
          </Button>
        </Stack>

        {loading ? (
          <Skeleton variant="rounded" height={420} />
        ) : (
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            {/* Phones get stacked cards; the wide grid-table needs >= md. */}
            <Stack sx={{ display: { xs: 'flex', md: 'none' } }}>
              {filteredMembers.map((member, idx) => (
                <Box
                  key={member.member_id}
                  sx={{
                    p: 2,
                    borderBottom: idx === filteredMembers.length - 1 ? 0 : 1,
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Avatar
                      src={member.photo_data_url || undefined}
                      alt={member.name}
                      sx={{
                        width: 34,
                        height: 34,
                        fontSize: 13,
                        fontWeight: 600,
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.16),
                        color: 'primary.main',
                      }}
                    >
                      {initials(member.name)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {member.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {member.email}
                        {member.phone ? ` · ${member.phone}` : ''}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => openEdit(member)}
                      aria-label="Edit member"
                    >
                      <EditIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                    <StatusCell status={member.status} />
                    {member.tags?.map((tag, i) => (
                      <TagPill key={i} tag={tag} />
                    ))}
                  </Stack>
                </Box>
              ))}
              {filteredMembers.length === 0 && (
                <Typography align="center" color="text.secondary" variant="body2" sx={{ py: 6 }}>
                  No members match your search.
                </Typography>
              )}
            </Stack>

            <Box sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
              <Box sx={{ minWidth: 960 }}>
                <Box sx={{ ...ROW_COLUMNS, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  {['Member', 'Contact', 'Tags', 'Status', 'Joined', 'Last visit', ''].map((h) => (
                    <Typography
                      key={h || 'actions'}
                      variant="caption"
                      sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: '0.06em' }}
                    >
                      {h === 'Tags' ? (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          {h}
                          <Tooltip title={TAGS_EXPLAINER} arrow placement="top">
                            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          </Tooltip>
                        </Box>
                      ) : (
                        h
                      )}
                    </Typography>
                  ))}
                </Box>

                {filteredMembers.map((member, idx) => (
                  <Box
                    key={member.member_id}
                    data-testid={`member-row-${member.member_id}`}
                    sx={{
                      ...ROW_COLUMNS,
                      py: 1.25,
                      borderBottom: idx === filteredMembers.length - 1 ? 0 : 1,
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                      transition: 'background-color 120ms ease',
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                      <Avatar
                        src={member.photo_data_url || undefined}
                        alt={member.name}
                        sx={{
                          width: 30,
                          height: 30,
                          fontSize: 12,
                          fontWeight: 600,
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.16),
                          color: 'primary.main',
                        }}
                      >
                        {initials(member.name)}
                      </Avatar>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {member.name}
                      </Typography>
                    </Stack>

                    <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                      {member.email}
                      {member.phone ? ` · ${member.phone}` : ''}
                    </Typography>

                    <Stack direction="row" spacing={0.75} sx={{ minWidth: 0, overflow: 'hidden' }}>
                      {member.tags?.length > 0 ? (
                        member.tags.map((tag, i) => <TagPill key={i} tag={tag} />)
                      ) : (
                        <Typography variant="body2" sx={{ color: 'action.disabled' }}>
                          —
                        </Typography>
                      )}
                    </Stack>

                    <StatusCell status={member.status} />

                    <Typography variant="body2" color="text.secondary" noWrap>
                      {formatDate(member.date_joined)}
                    </Typography>

                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ color: member.last_appointment_at ? 'text.secondary' : 'action.disabled' }}
                    >
                      {formatDate(member.last_appointment_at)}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={() => openEdit(member)}
                      aria-label="Edit member"
                      data-testid={`edit-member-${member.member_id}`}
                    >
                      <EditIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Box>
                ))}

                {filteredMembers.length === 0 && (
                  <Typography align="center" color="text.secondary" variant="body2" sx={{ py: 6 }}>
                    No members match your search.
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    </MainLayout>
  );
};

export default MembersPage;
